import { useUser, SignOutButton } from 'headlo-auth'
import { useShopContext } from '../ShopContext'

// Settings — signed-in-user info, sign out, and shop switcher (when the
// merchant has 2+ shops). Push notification toggle lands in Wave 4e.

export function SettingsScreen() {
  const user = useUser()
  const { state, select } = useShopContext()

  // Only useful when the merchant has 2+ shops. Single-shop merchants
  // don't get the section — nothing to switch to.
  const shops = state.status === 'ready' || state.status === 'needs-picker' ? state.shops : []
  const currentHandle = state.status === 'ready' ? state.shop.handle : null

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Settings</h1>

      {/* No-shops explainer — merchant landed here because Orders/Earnings
          have nothing to show. Also visible if list is briefly empty during
          error/idle transitions, which is fine — soft message. */}
      {state.status === 'no-shops' && (
        <section style={s.section}>
          <div style={s.card}>
            <div style={s.noShopsTitle}>No shops yet</div>
            <div style={s.noShopsMsg}>
              Create your first shop at <strong>headlo.com</strong>, then
              reopen this app to see orders and earnings.
            </div>
          </div>
        </section>
      )}

      {shops.length > 1 && (
        <section style={s.section}>
          <div style={s.sectionLabel}>Current shop</div>
          <div style={s.card}>
            {shops.map(shop => {
              const active = shop.handle === currentHandle
              return (
                <button
                  key={shop.handle}
                  onClick={() => select(shop.handle)}
                  style={{ ...s.shopRow, ...(active ? s.shopRowActive : {}) }}
                >
                  <span style={s.shopName}>{shop.brand_name || shop.handle}</span>
                  <span style={s.shopMark}>{active ? '✓' : ''}</span>
                </button>
              )
            })}
          </div>
        </section>
      )}

      <section style={s.section}>
        <div style={s.sectionLabel}>Account</div>
        <div style={s.card}>
          <div style={s.email}>{user?.email ?? '—'}</div>
          <SignOutButton>
            <button style={s.signOutBtn}>Sign out</button>
          </SignOutButton>
        </div>
      </section>

      <section style={s.section}>
        <div style={s.sectionLabel}>About</div>
        <div style={s.card}>
          <div style={s.aboutRow}>
            <span style={s.aboutLabel}>App version</span>
            <span style={s.aboutValue}>v0.1.0</span>
          </div>
        </div>
      </section>
    </div>
  )
}

const s = {
  wrap: {
    padding: '24px 20px',
  },
  h1: {
    fontSize: 28,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#8a8a80',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    padding: '0 4px 8px',
  },
  card: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e5e5e0',
    padding: 16,
  },
  email: {
    fontSize: 15,
    marginBottom: 16,
    fontFamily: '"SF Mono", Consolas, monospace',
  },
  signOutBtn: {
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    background: 'transparent',
    color: '#c14a4a',
    border: '1px solid #c14a4a',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  aboutRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  aboutLabel: {
    fontSize: 14,
    color: '#5a5a55',
  },
  aboutValue: {
    fontSize: 14,
    fontFamily: '"SF Mono", Consolas, monospace',
    color: '#1a1a18',
  },
  shopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '10px 4px',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #f0efe9',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
  },
  shopRowActive: {
    fontWeight: 600,
  },
  shopName: {
    fontSize: 15,
    color: '#1a1a18',
    flex: 1,
  },
  shopMark: {
    fontSize: 16,
    color: '#2a6a3a',
    fontWeight: 700,
    marginLeft: 10,
  },
  noShopsTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a18',
    marginBottom: 6,
  },
  noShopsMsg: {
    fontSize: 13,
    color: '#5a5a55',
    lineHeight: 1.55,
  },
} as const
